// Organization settings (ORG-01, ORG-02): name and time zone, working hours, follow-up rules, the digest,
// holidays and numbers never to record.
import { type FormEvent, useState } from "react";
import { fmtDay, fmtPhone } from "../lib/format";
import {
  DAY_LABEL,
  DAYS,
  type OrgSettings,
  parseSettings,
  parseWorkingHours,
  type RetryMode,
  settingsJson,
  settingsProblem,
  TIMEZONES,
  type UnansweredChoice,
  type WorkingHours,
  workingHoursProblem,
} from "../lib/settings";
import type { Json } from "../lib/database.types";
import { type VisitSettings, visitSettingsProblem } from "../lib/visits";
import {
  filledMessage,
  MESSAGE_PLACEHOLDERS,
  type MessageDetails,
  messageProblem,
  type MessageTemplate,
  type MessageTemplateInput,
} from "../lib/crm";
import {
  ACTS_AS,
  type ActsAs,
  type Disposition,
  type DispositionCall,
  dispositionsProblem,
  STANDARD,
  UNANSWERED_ACTS,
} from "../lib/dispositions";
import { useMember, useSession } from "../app/session";
import { Card, ConfirmButton, Empty, ErrorNote, Field, Loading, Modal, Page, useAction, useLoad } from "../app/ui";

export function Settings() {
  const { api } = useMember();
  const { data, error, loading, reload } = useLoad(() => api.settings(), [api]);
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;
  return (
    <Page title="Settings" sub={data.org.name}>
      <div className="stack">
        <General
          key={`g${data.org.updated_at}`}
          name={data.org.name}
          timezone={data.org.timezone}
          onSaved={reload}
        />
        <Hours
          key={`h${data.org.updated_at}`}
          initial={parseWorkingHours(data.org.working_hours)}
          onSaved={reload}
        />
        <FollowUps
          key={`s${data.org.updated_at}`}
          initial={parseSettings(data.org.settings)}
          onSaved={reload}
        />
        <Visits
          key={`v${data.org.updated_at}`}
          initial={parseSettings(data.org.settings)}
          onSaved={reload}
        />
        <WhatsAppMessages orgName={data.org.name} />
        <DispositionSettings />
        <div className="grid cols-2">
          <Holidays rows={data.holidays} onChanged={reload} />
          <Excluded rows={data.excluded} onChanged={reload} />
        </div>
      </div>
    </Page>
  );
}

function General(
  props: { name: string; timezone: string; onSaved: () => Promise<void> },
) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [name, setName] = useState(props.name);
  const [tz, setTz] = useState(props.timezone);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (
      await run(() => api.saveOrg({ name: name.trim(), timezone: tz }), "Saved")
    ) await props.onSaved();
  };
  return (
    <Card title="Company">
      <form onSubmit={submit} className="grid cols-3">
        <Field label="Name">
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Time zone">
          <select value={tz} onChange={(e) => setTz(e.target.value)}>
            {[...new Set([tz, ...TIMEZONES])].map((z) => <option key={z}>{z}</option>)}
          </select>
        </Field>
        <div className="field">
          <span>&nbsp;</span>
          <button className="primary" disabled={busy}>Save</button>
        </div>
      </form>
    </Card>
  );
}

function Hours(props: { initial: WorkingHours; onSaved: () => Promise<void> }) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [hours, setHours] = useState<WorkingHours>(props.initial);
  const problem = workingHoursProblem(hours);

  const setWindow = (
    day: (typeof DAYS)[number],
    i: number,
    which: 0 | 1,
    value: string,
  ) =>
    setHours((h) => {
      const windows = [...(h[day] ?? [])].map((w) => [...w] as [string, string]);
      windows[i][which] = value;
      return { ...h, [day]: windows };
    });

  const save = async () => {
    if (
      await run(
        () => api.saveOrg({ working_hours: hours as unknown as Json }),
        "Working hours saved",
      )
    ) {
      await props.onSaved();
    }
  };

  return (
    <Card
      title="Working hours"
      actions={
        <button
          className="primary small"
          disabled={busy || !!problem}
          onClick={() => void save()}
        >
          Save
        </button>
      }
    >
      <p className="faint">
        Follow-ups are only scheduled inside these hours, and never on holidays.
      </p>
      {DAYS.map((day) => {
        const windows = hours[day] ?? [];
        return (
          <div className="day-row" key={day}>
            <strong>{DAY_LABEL[day]}</strong>
            <label className="row">
              <input
                type="checkbox"
                checked={windows.length > 0}
                onChange={(e) =>
                  setHours((h) => ({
                    ...h,
                    [day]: e.target.checked ? [["10:00", "19:00"]] : [],
                  }))}
              />
              Open
            </label>
            <div className="windows">
              {windows.map((w, i) => (
                <span className="row" key={i}>
                  <input
                    type="time"
                    value={w[0]}
                    onChange={(e) => setWindow(day, i, 0, e.target.value)}
                    aria-label={`${DAY_LABEL[day]} from`}
                  />
                  –
                  <input
                    type="time"
                    value={w[1]}
                    onChange={(e) => setWindow(day, i, 1, e.target.value)}
                    aria-label={`${DAY_LABEL[day]} until`}
                  />
                  {windows.length > 1 && (
                    <button
                      className="small ghost"
                      onClick={() =>
                        setHours((h) => ({
                          ...h,
                          [day]: windows.filter((_, j) => j !== i),
                        }))}
                    >
                      ✕
                    </button>
                  )}
                </span>
              ))}
              {windows.length > 0 && windows.length < 4 && (
                <button
                  className="small ghost"
                  onClick={() =>
                    setHours((h) => ({
                      ...h,
                      [day]: [...windows, ["15:00", "19:00"]],
                    }))}
                >
                  + Add a break
                </button>
              )}
              {windows.length === 0 && <span className="faint">Closed</span>}
            </div>
          </div>
        );
      })}
      {problem && <div className="error-note" style={{ marginTop: 10 }}>{problem}</div>}
    </Card>
  );
}

function FollowUps(
  props: { initial: OrgSettings; onSaved: () => Promise<void> },
) {
  const { api, member } = useMember();
  const { busy, run } = useAction();
  const [s, setS] = useState<OrgSettings>(props.initial);
  const problem = settingsProblem(s);
  const addOn = Boolean(member.features?.coach || member.features?.autopilot);
  const fu = s.follow_up;
  const set = (patch: Partial<OrgSettings["follow_up"]>) =>
    setS((x) => ({ ...x, follow_up: { ...x.follow_up, ...patch } }));
  const num = (v: string) => (v === "" ? Number.NaN : Number(v));
  const save = async () => {
    if (
      await run(
        () => api.saveOrg({ settings: settingsJson(s) }),
        "Settings saved",
      )
    ) await props.onSaved();
  };

  return (
    <Card
      title="Follow-ups, digest and recordings"
      actions={
        <button
          className="primary small"
          disabled={busy || !!problem}
          onClick={() => void save()}
        >
          Save
        </button>
      }
    >
      <div className="grid cols-3">
        <Field
          label="Default follow-up time"
          hint="When a customer says “later” or “tomorrow”."
        >
          <input
            type="time"
            value={fu.default_time}
            onChange={(e) => set({ default_time: e.target.value })}
          />
        </Field>
        <Field
          label="Calls allowed from"
          hint="Earliest time for a follow-up the customer asked for."
        >
          <input
            type="time"
            value={fu.calling_from}
            onChange={(e) => set({ calling_from: e.target.value })}
          />
        </Field>
        <Field label="Calls allowed until">
          <input
            type="time"
            value={fu.calling_until}
            onChange={(e) => set({ calling_until: e.target.value })}
          />
        </Field>
        <Field label="“Morning” means">
          <input
            type="time"
            value={fu.morning}
            onChange={(e) => set({ morning: e.target.value })}
          />
        </Field>
        <Field label="“Afternoon” means">
          <input
            type="time"
            value={fu.afternoon}
            onChange={(e) => set({ afternoon: e.target.value })}
          />
        </Field>
        <Field label="“Evening” means">
          <input
            type="time"
            value={fu.evening}
            onChange={(e) => set({ evening: e.target.value })}
          />
        </Field>
        <Field
          label="When do you want the next attempt after a call nobody answered?"
          hint={fu.retry_mode === "next_day"
            ? "Never the same day: the next working day, a morning call in the afternoon, an afternoon call in the evening and an evening call in the morning, at the times above."
            : undefined}
        >
          <select
            value={fu.retry_mode}
            onChange={(e) => set({ retry_mode: e.target.value as RetryMode })}
          >
            <option value="minutes">After a number of minutes</option>
            <option value="next_day">The next day, at another time of day</option>
          </select>
        </Field>
        {fu.retry_mode === "minutes" && (
          <Field label="Retry an unanswered call after (minutes)">
            <input
              type="number"
              min={15}
              max={1440}
              value={fu.retry_minutes}
              onChange={(e) => set({ retry_minutes: num(e.target.value) })}
            />
          </Field>
        )}
        <Field
          label="When does Voxowa stop retrying?"
          hint={fu.retry_until_answered
            ? "It keeps trying until the customer answers. Mark a customer lost or not a lead to stop."
            : undefined}
        >
          <select
            value={fu.retry_until_answered ? "never" : "after"}
            onChange={(e) => set({ retry_until_answered: e.target.value === "never" })}
          >
            <option value="after">After a number of unanswered calls</option>
            <option value="never">Never: keep trying until they answer</option>
          </select>
        </Field>
        {!fu.retry_until_answered && (
          <Field
            label="Stop retrying after (unanswered calls)"
            hint="In a row; then one last try a week after the last call, and no more."
          >
            <input
              type="number"
              min={0}
              max={10}
              value={fu.max_attempts}
              onChange={(e) => set({ max_attempts: num(e.target.value) })}
            />
          </Field>
        )}
        <Field label="Follow up a quiet lead after (days)">
          <input
            type="number"
            min={1}
            max={90}
            value={fu.quiet_after_days}
            onChange={(e) => set({ quiet_after_days: num(e.target.value) })}
          />
        </Field>
      </div>
      <label className="row" style={{ marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={fu.missed_call_rule}
          onChange={(e) => set({ missed_call_rule: e.target.checked })}
        />
        Missed call from a lead: call back within 10 minutes
      </label>
      <div className="grid cols-3">
        <Field
          label="Morning digest"
          hint="Yesterday's numbers, hot leads and overdue follow-ups."
        >
          <label className="row">
            <input
              type="checkbox"
              checked={s.digest.enabled}
              onChange={(e) =>
                setS((x) => ({
                  ...x,
                  digest: { ...x.digest, enabled: e.target.checked },
                }))}
            />
            Send every morning
          </label>
        </Field>
        <Field label="Digest time">
          <input
            type="time"
            value={s.digest.time}
            onChange={(e) =>
              setS((x) => ({
                ...x,
                digest: { ...x.digest, time: e.target.value },
              }))}
          />
        </Field>
        <Field
          label="Keep call audio for (days)"
          hint="7 to 730. Transcripts and AI notes stay after the audio is deleted."
        >
          <input
            type="number"
            min={7}
            max={730}
            value={s.retention.audio_days}
            onChange={(e) =>
              setS((x) => ({
                ...x,
                retention: { audio_days: e.target.value === "" ? Number.NaN : Number(e.target.value) },
              }))}
          />
        </Field>
        <Field
          label="Call recordings"
          hint={addOn
            ? "Coach or Autopilot reviews your calls, so your phones send their recordings."
            : s.phones.sync_recordings === props.initial.phones.sync_recordings
            ? "Without Coach or Autopilot no AI hears them. Kept, each plays on its call, until the audio is deleted."
            : s.phones.sync_recordings
            ? "When you save, each person reads the recording notice again in the app; their phone pauses sending " +
              "calls until they do."
            : "When you save, phones stop sending recordings. Those already sent stay until the audio is deleted."}
        >
          {addOn ? <span className="muted">Sent for call review</span> : (
            <label className="brief-switch">
              <input
                type="checkbox"
                checked={s.phones.sync_recordings}
                onChange={(e) => setS((x) => ({ ...x, phones: { ...x.phones, sync_recordings: e.target.checked } }))}
              />
              Keep call recordings, with no AI
            </label>
          )}
        </Field>
        <Field
          label="When a phone is set up, send its last"
          hint="Calls and recordings from before this are never sent. The recording notice tells telecallers."
        >
          <select
            value={s.phones.history_days}
            onChange={(e) =>
              setS((x) => ({
                ...x,
                phones: { ...x.phones, history_days: e.target.value === "7" ? 7 : 3 },
              }))}
          >
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
          </select>
        </Field>
        <Field
          label="When a colleague's customer calls a telecaller"
          hint="Her phone names them while it rings. With this on, it also says whose customer they are and their last conversation, for when a colleague is away or the customer calls round. Off, it only says a colleague's customer is calling."
        >
          <label className="brief-switch">
            <input
              type="checkbox"
              checked={s.phones.colleague_customers}
              onChange={(e) => setS((x) => ({ ...x, phones: { ...x.phones, colleague_customers: e.target.checked } }))}
            />
            Show whose customer, and their last conversation
          </label>
        </Field>
        <Field
          label="After a call nobody answered"
          hint="A call a telecaller placed that nobody picked up is certainly not answered. Either way it counts as not answered, the retry rule sets one reminder to call again, and she is never asked for a time. When the phone logged seconds but only an operator's message or voicemail played, she taps Not connected herself."
        >
          <select
            value={s.phones.unanswered}
            onChange={(e) =>
              setS((x) => ({
                ...x,
                phones: { ...x.phones, unanswered: e.target.value as UnansweredChoice },
              }))}
          >
            <option value="save">Save it as not answered, and show that for two seconds</option>
            <option value="ask">Ask her, with Not connected first</option>
            <option value="silent">Save it without showing anything</option>
          </select>
        </Field>
      </div>
      {problem && <div className="error-note">{problem}</div>}
    </Card>
  );
}

/** Who handles a booked visit, where visits happen and the call to confirm one (D-186). Nothing set, visits stay with
 * whoever books them, as before. The message sent on booking one is a WhatsApp message marked for visits (D-187). */
function Visits(props: { initial: OrgSettings; onSaved: () => Promise<void> }) {
  const { api } = useMember();
  // The signed-in person's own status carries the visit settings for booking one: refreshed after a save.
  const { reload: reloadStatus } = useSession();
  const members = useLoad(() => api.members(), [api]);
  const { busy, run } = useAction();
  const [s, setS] = useState<OrgSettings>(props.initial);
  const v = s.visits;
  const set = (patch: Partial<VisitSettings>) => setS((x) => ({ ...x, visits: { ...x.visits, ...patch } }));
  const problem = visitSettingsProblem(v);
  const team = (members.data ?? []).filter((m) => m.status === "active");
  const save = async () => {
    if (await run(() => api.saveOrg({ settings: settingsJson(s) }), "Visit settings saved")) {
      await Promise.all([props.onSaved(), reloadStatus()]);
    }
  };

  return (
    <Card
      title="Visits"
      actions={
        <button className="primary small" disabled={busy || !!problem} onClick={() => void save()}>
          Save
        </button>
      }
    >
      <p className="faint" style={{ marginTop: 0 }}>
        When a telecaller books a visit: who meets the customer, where, and the call to confirm it. Whoever booked it
        keeps the credit. The message she sends the customer is one of your WhatsApp messages marked for visits.
      </p>
      <div className="grid cols-2">
        <Field label="Who handles visits">
          <select
            value={v.handler}
            onChange={(e) => set({ handler: e.target.value as VisitSettings["handler"] })}
          >
            <option value="booker">Whoever books the visit</option>
            <option value="one">One person</option>
            <option value="in_turn">In turn, whoever is free then</option>
          </select>
        </Field>
        {v.handler === "one" && (
          <Field label="Who" hint="Every visit booked goes to them; they can still give one to a colleague.">
            <select
              value={v.handler_member ?? ""}
              onChange={(e) =>
                set({ handler_member: e.target.value || null })}
            >
              <option value="">Choose…</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
          </Field>
        )}
        {v.handler === "in_turn" && (
          <Field label="Who takes visits" hint="Whoever is free at that time, then whoever has had fewest that day.">
            <div className="stack tight">
              {team.map((m) => (
                <label key={m.id} className="row" style={{ gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={v.handler_members.includes(m.id)}
                    onChange={(e) =>
                      set({
                        handler_members: e.target.checked
                          ? [...v.handler_members, m.id]
                          : v.handler_members.filter((x) => x !== m.id),
                      })}
                  />
                  {m.display_name}
                </label>
              ))}
            </div>
          </Field>
        )}
        <Field label="Where visits happen" hint="Used when the telecaller names no other place.">
          <input
            value={v.place}
            maxLength={300}
            placeholder="Your office or centre's address"
            onChange={(e) => set({ place: e.target.value })}
          />
        </Field>
        <Field label="Map link (optional)" hint="A Google Maps link, sent with the message.">
          <input
            value={v.map_link}
            maxLength={500}
            placeholder="https://maps.app.goo.gl/…"
            onChange={(e) => set({ map_link: e.target.value })}
          />
        </Field>
        <Field
          label="Call to confirm the visit"
          hint="For whoever booked it. Moved with the visit; after a no-show, a call to book it again."
        >
          <div className="row" style={{ gap: 8 }}>
            <select
              value={v.reminder ?? "off"}
              onChange={(e) => set({ reminder: e.target.value as VisitSettings["reminder"] })}
            >
              <option value="off">Off</option>
              <option value="hours_before">Some hours before</option>
              <option value="evening_before">The evening before (6 pm)</option>
            </select>
            {v.reminder === "hours_before" && (
              <select
                aria-label="Hours before"
                value={v.reminder_hours}
                onChange={(e) => set({ reminder_hours: Number(e.target.value) })}
              >
                {[1, 2, 3, 4, 5, 6].map((h) => <option key={h} value={h}>{h} {h === 1 ? "hour" : "hours"}</option>)}
              </select>
            )}
          </div>
        </Field>
      </div>
      {problem && <div className="error-note">{problem}</div>}
    </Card>
  );
}

/**
 * The company's dispositions (D-188): what a telecaller picks after an answered call and after one nobody picked up, in
 * its own words, each working as one of the outcomes the CRM acts on. With none, the standard choices.
 */
function DispositionSettings() {
  const { api } = useMember();
  const lists = useLoad(() => api.dispositions(), [api]);
  return (
    <Card title="Dispositions">
      <p className="faint" style={{ marginTop: 0 }}>
        What a telecaller picks after a call, in your words: a hospital's are not a training centre's. Each works as one
        of Voxowa's outcomes, so reminders, visits and reports carry on as before. Phones pick up a change the next time
        they refresh, and keep the list for when they are offline.
      </p>
      {lists.loading && !lists.data && <Loading />}
      <ErrorNote error={lists.error} />
      {lists.data && (
        <div className="grid cols-2">
          <DispositionList
            key={`a${lists.data.answered.map((d) => d.id).join()}`}
            call="answered"
            initial={lists.data.answered}
            onSaved={lists.reload}
          />
          <DispositionList
            key={`u${lists.data.unanswered.map((d) => d.id).join()}`}
            call="unanswered"
            initial={lists.data.unanswered}
            onSaved={lists.reload}
          />
        </div>
      )}
    </Card>
  );
}

/** One of the two lists, edited and saved whole. */
function DispositionList(props: { call: DispositionCall; initial: Disposition[]; onSaved: () => Promise<void> }) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [list, setList] = useState<Disposition[]>(props.initial);
  const own = props.initial.length > 0;
  const editing = list.length > 0;
  const options = ACTS_AS.filter((a) => props.call === "answered" || UNANSWERED_ACTS.includes(a.value));
  const problem = dispositionsProblem(props.call, list);
  const set = (i: number, patch: Partial<Disposition>) =>
    setList(list.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const move = (i: number, by: -1 | 1) => {
    const next = [...list];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    setList(next);
  };
  const save = async (next: Disposition[]) => {
    if (await run(() => api.saveDispositions(props.call, next), "Dispositions saved")) await props.onSaved();
  };
  const changed = JSON.stringify(list) !== JSON.stringify(props.initial);

  return (
    <div className="stack tight">
      <strong>{props.call === "answered" ? "After an answered call" : "After a call nobody answered"}</strong>
      {!editing && (
        <>
          <span className="faint">
            {props.call === "answered"
              ? `The standard choices: ${STANDARD.answered.map((d) => d.label).join(", ")}.`
              : "The standard: the phone saves it as not answered by itself, and the call is tried again by the rule."}
          </span>
          <div>
            <button
              className="small"
              onClick={() => setList(STANDARD[props.call].map((d) => ({ ...d })))}
            >
              Write your own
            </button>
          </div>
        </>
      )}
      {props.call === "unanswered" && editing && (
        <span className="faint">
          With these, phones ask why after a call nobody picked up, instead of saving it by themselves.
        </span>
      )}
      {list.map((d, i) => (
        <div key={d.id ?? `new${i}`} className="list-item stack tight">
          <div className="row" style={{ gap: 6 }}>
            <input
              aria-label="Name"
              value={d.label}
              maxLength={40}
              placeholder={props.call === "answered" ? "Appointment booked" : "Switched off"}
              onChange={(e) => set(i, { label: e.target.value })}
            />
            <button
              className="small ghost"
              aria-label={`Move ${d.label} up`}
              disabled={i === 0}
              onClick={() => move(i, -1)}
            >
              ↑
            </button>
            <button
              className="small ghost"
              aria-label={`Move ${d.label} down`}
              disabled={i === list.length - 1}
              onClick={() => move(i, 1)}
            >
              ↓
            </button>
            <button
              className="small ghost"
              aria-label={`Remove ${d.label}`}
              onClick={() => setList(list.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <select
              aria-label="Works as"
              value={d.acts_as}
              onChange={(e) => set(i, { acts_as: e.target.value as ActsAs })}
            >
              {options.map((a) => <option key={a.value} value={a.value}>Works as {a.label}</option>)}
            </select>
            <input
              aria-label="In Hindi"
              value={d.label_hi}
              maxLength={40}
              placeholder="In Hindi (optional)"
              onChange={(e) => set(i, { label_hi: e.target.value })}
            />
          </div>
          <span className="faint">{ACTS_AS.find((a) => a.value === d.acts_as)?.does}</span>
        </div>
      ))}
      {editing && (
        <div className="row wrap" style={{ gap: 6 }}>
          <button
            className="small"
            disabled={list.length >= 20}
            onClick={() =>
              setList([...list, {
                label: "",
                label_hi: "",
                acts_as: props.call === "answered" ? "not_now" : "not_connected",
              }])}
          >
            Add a choice
          </button>
          <button className="primary small" disabled={busy || !!problem || !changed} onClick={() => void save(list)}>
            Save
          </button>
          {own
            ? (
              <ConfirmButton
                label="Back to the standard"
                confirm="Use the standard choices?"
                disabled={busy}
                onConfirm={() => void save([])}
              />
            )
            : <button className="small ghost" onClick={() => setList([])}>Cancel</button>}
        </div>
      )}
      {problem && list.some((d) => d.label.trim()) && <div className="error-note">{problem}</div>}
    </div>
  );
}

/** A made-up visit, to show how a message reads. */
function sampleVisit(
  member: { display_name: string; visits?: { place: string | null; map_link: string | null } | null },
): MessageDetails {
  return {
    day: "Friday 24 Oct",
    time: "4:00 pm",
    place: member.visits?.place || "Your centre's address",
    map: member.visits?.map_link || "https://maps.app.goo.gl/…",
    handler: member.display_name,
  };
}

/**
 * The company's WhatsApp messages (C7, D-187), named and in its order: its people send them from their own WhatsApp,
 * from a customer's page or after a call. One marked for visits is offered after booking a visit, with the visit's
 * details filled in, so a centre visit and an online workshop each get their own words.
 */
function WhatsAppMessages(props: { orgName: string }) {
  const { api, member } = useMember();
  const list = useLoad(() => api.messageTemplates(), [api]);
  const { busy, run } = useAction();
  const [editing, setEditing] = useState<MessageTemplate | "new" | null>(null);
  const rows = list.data ?? [];
  const sample = { ...sampleVisit(member), company: props.orgName };
  const move = async (i: number, by: -1 | 1) => {
    const ids = rows.map((t) => t.id);
    [ids[i], ids[i + by]] = [ids[i + by], ids[i]];
    if (await run(() => api.orderMessageTemplates(ids), "Order saved")) await list.reload();
  };
  const remove = async (id: string) => {
    if (await run(() => api.removeMessageTemplate(id), "Message removed")) await list.reload();
  };
  return (
    <Card
      title="WhatsApp messages"
      actions={
        <button className="primary small" onClick={() => setEditing("new")}>
          Add a message
        </button>
      }
    >
      <p className="faint" style={{ marginTop: 0 }}>
        Prepared messages your team sends from their own WhatsApp, from a customer's page or after a call. Mark one for
        visits to offer it after booking a visit, with the day, time and place filled in: a centre visit, an online
        workshop.
      </p>
      {list.loading && !list.data && <Loading />}
      <ErrorNote error={list.error} />
      {list.data && rows.length === 0 && (
        <Empty>No messages yet. Without one for visits, the standard visit message is offered.</Empty>
      )}
      <div className="list">
        {rows.map((t, i) => (
          <div key={t.id} className="list-item">
            <div className="row between wrap">
              <div className="row">
                <strong>{t.name}</strong>
                {t.for_visits && <span className="badge brand">For visits</span>}
                {t.body.hi && <span className="badge">Hindi too</span>}
              </div>
              <div className="row">
                <button
                  className="small ghost"
                  aria-label={`Move ${t.name} up`}
                  disabled={busy || i === 0}
                  onClick={() => void move(i, -1)}
                >
                  ↑
                </button>
                <button
                  className="small ghost"
                  aria-label={`Move ${t.name} down`}
                  disabled={busy || i === rows.length - 1}
                  onClick={() => void move(i, 1)}
                >
                  ↓
                </button>
                <button className="small" onClick={() => setEditing(t)}>Edit</button>
                <ConfirmButton
                  label="Remove"
                  confirm="No longer offer it?"
                  disabled={busy}
                  onConfirm={() => void remove(t.id)}
                />
              </div>
            </div>
            <span className="faint template-body">
              {filledMessage(
                t,
                "en",
                "Priya Sharma",
                member.display_name,
                t.for_visits ? sample : { company: sample.company },
              )}
            </span>
          </div>
        ))}
      </div>
      {editing && (
        <MessageModal
          start={editing === "new" ? null : editing}
          sample={sample}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await list.reload();
          }}
        />
      )}
    </Card>
  );
}

/** One WhatsApp message written or changed: its name, its words in English and Hindi, a link, and whether it is
 * offered after booking a visit. */
function MessageModal(
  props: { start: MessageTemplate | null; sample: MessageDetails; onClose: () => void; onSaved: () => Promise<void> },
) {
  const { api, member } = useMember();
  const { busy, run } = useAction();
  const [t, setT] = useState<MessageTemplateInput>(() => ({
    id: props.start?.id ?? null,
    name: props.start?.name ?? "",
    body: { en: props.start?.body.en ?? "", hi: props.start?.body.hi ?? "" },
    link: props.start?.link ?? "",
    for_visits: props.start?.for_visits ?? false,
  }));
  const setBody = (lang: "en" | "hi", text: string) => setT((x) => ({ ...x, body: { ...x.body, [lang]: text } }));
  const problem = messageProblem(t);
  const preview = filledMessage(
    { id: "", name: t.name, body: t.body, link: t.link?.trim() || null, for_visits: t.for_visits },
    t.body.en?.trim() ? "en" : "hi",
    "Priya Sharma",
    member.display_name,
    t.for_visits ? props.sample : { company: props.sample.company },
  );
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (await run(() => api.saveMessageTemplate({ ...t, link: t.link?.trim() || null }), "Message saved")) {
      await props.onSaved();
    }
  };
  return (
    <Modal title={props.start ? "Change the message" : "A new message"} onClose={props.onClose}>
      <form className="stack" onSubmit={(e) => void save(e)}>
        <Field label="Name" hint="What your team picks it by, such as Centre visit or Online workshop.">
          <input value={t.name} maxLength={60} autoFocus onChange={(e) => setT({ ...t, name: e.target.value })} />
        </Field>
        <Field
          label="Message"
          hint={`${
            MESSAGE_PLACEHOLDERS.map(([k, what]) => `${k} ${what}`).join("; ")
          }. A line whose detail is missing is left out.`}
        >
          <textarea rows={5} maxLength={1000} value={t.body.en} onChange={(e) => setBody("en", e.target.value)} />
        </Field>
        <Field label="In Hindi (optional)" hint="Sent by those whose app is in Hindi.">
          <textarea rows={3} maxLength={1000} value={t.body.hi} onChange={(e) => setBody("hi", e.target.value)} />
        </Field>
        <Field label="Link (optional)" hint="A brochure, a price list or a meeting link, sent last.">
          <input
            value={t.link ?? ""}
            maxLength={500}
            placeholder="https://…"
            onChange={(e) => setT({ ...t, link: e.target.value })}
          />
        </Field>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={t.for_visits}
            onChange={(e) => setT({ ...t, for_visits: e.target.checked })}
          />
          Offer it after booking a visit
        </label>
        <div className="faint">How it reads</div>
        <div className="message-preview">{preview || "–"}</div>
        {problem && (t.name || t.body.en || t.body.hi) && <div className="error-note">{problem}</div>}
        <div className="actions">
          <button type="button" onClick={props.onClose}>Cancel</button>
          <button className="primary" disabled={busy || !!problem}>Save</button>
        </div>
      </form>
    </Modal>
  );
}

function Holidays(
  props: {
    rows: { id: string; day: string; name: string | null }[];
    onChanged: () => Promise<void>;
  },
) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [day, setDay] = useState("");
  const [name, setName] = useState("");
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (await run(() => api.addHoliday(day, name.trim()), "Holiday added")) {
      setDay("");
      setName("");
      await props.onChanged();
    }
  };
  return (
    <Card title="Holidays">
      <form className="row" onSubmit={add} style={{ marginBottom: 10 }}>
        <input
          type="date"
          required
          value={day}
          onChange={(e) => setDay(e.target.value)}
          aria-label="Date"
        />
        <input
          placeholder="Diwali"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Name"
        />
        <button disabled={busy || !day}>Add</button>
      </form>
      <div className="list">
        {props.rows.map((h) => (
          <div className="list-item row between" key={h.id}>
            <span>
              {fmtDay(h.day)} <span className="faint">{h.name}</span>
            </span>
            <ConfirmButton
              label="Remove"
              confirm="Remove?"
              disabled={busy}
              onConfirm={async () => {
                if (
                  await run(() => api.deleteHoliday(h.id), "Removed")
                ) {
                  await props.onChanged();
                }
              }}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}

function Excluded(
  props: {
    rows: { id: string; phone_e164: string; reason: string | null }[];
    onChanged: () => Promise<void>;
  },
) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (
      await run(
        () => api.addExcluded(phone.trim(), reason.trim()),
        "Number excluded",
      )
    ) {
      setPhone("");
      setReason("");
      await props.onChanged();
    }
  };
  return (
    <Card title="Never record these numbers">
      <p className="faint">
        Family, doctors, the office landline. Their calls are dropped on the phone and never uploaded.
      </p>
      <form className="row" onSubmit={add} style={{ marginBottom: 10 }}>
        <input
          type="tel"
          required
          placeholder="98765 43210"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          aria-label="Number"
        />
        <input
          placeholder="Family"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-label="Reason"
        />
        <button disabled={busy || !phone.trim()}>Add</button>
      </form>
      <div className="list">
        {props.rows.map((x) => (
          <div className="list-item row between" key={x.id}>
            <span>
              {fmtPhone(x.phone_e164)} <span className="faint">{x.reason}</span>
            </span>
            <ConfirmButton
              label="Remove"
              confirm="Remove?"
              disabled={busy}
              onConfirm={async () => {
                if (
                  await run(() => api.deleteExcluded(x.id), "Removed")
                ) {
                  await props.onChanged();
                }
              }}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
